import {
  buildCsv,
  csvRowCount,
  type CheckInTemplate,
  type CsvRequest,
  type CsvShape,
  type Entry,
  type Tag,
  type Track,
} from '@anytracker/core'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'

import { Button } from '../../components/Button.js'
import { useToast } from '../../components/Toast.js'
import { RangeControl } from '../../range/RangeControl.js'
import { useRange } from '../../range/RangeProvider.js'
import { useServices } from '../../services/ServicesProvider.js'
import styles from './Export.module.css'

interface ShapeChoice {
  readonly shape: CsvShape
  readonly title: string
  readonly detail: string
}

/**
 * Both shapes described by what they are *for*, and by what they cost.
 *
 * "Long" and "wide" are the names the format has; they are not names that tell
 * anyone which one to pick. The lossy half of the wide shape is stated here
 * rather than discovered afterwards in a spreadsheet with a column missing.
 */
const SHAPES: readonly ShapeChoice[] = [
  {
    shape: 'long',
    title: 'One row per measurement',
    detail:
      'Everything, including notes on individual values. This is the shape statistics software expects.',
  },
  {
    shape: 'wide',
    title: 'One row per entry',
    detail:
      'One column per track, easier to read in a spreadsheet. Notes attached to individual values are left out — there is nowhere to put them.',
  },
]

/**
 * CSV export (T-501).
 *
 * The escape hatch: it gets the data into a spreadsheet without depending on
 * this app. It is **not** a backup, and this screen says so plainly rather than
 * relying on the user to infer it — a CSV cannot be read back in, and finding
 * that out during a restore is finding it out too late (ADR-0003).
 *
 * Serialisation is core's; this screen chooses what to export and hands the
 * file to the browser.
 */
export function ExportScreen() {
  const { repos, exporter } = useServices()
  const { resolved, ready } = useRange()
  const toast = useToast()

  const [tracks, setTracks] = useState<Track[] | null>(null)
  const [entries, setEntries] = useState<Entry[]>([])
  const [tags, setTags] = useState<Tag[]>([])
  const [templates, setTemplates] = useState<CheckInTemplate[]>([])
  const [shape, setShape] = useState<CsvShape>('long')
  /**
   * Which tracks are *left out*, not which are included.
   *
   * Everything is exported by default, and a track created tomorrow is exported
   * too. An inclusion list silently stops covering tracks added after it was
   * last touched, which is an export that quietly loses data.
   */
  const [excludedIds, setExcludedIds] = useState<readonly string[]>([])
  const [busy, setBusy] = useState(false)

  const span = useMemo(
    () => ({ from: resolved.fromEpochMs, to: resolved.toEpochMs }),
    [resolved.fromEpochMs, resolved.toEpochMs],
  )

  const reload = useCallback(async () => {
    // Nothing is queried until the range is real — "all time" resolves to today
    // until the entry bounds have been read, and a count taken against that is
    // wrong and is about to be shown as a promise about the file.
    if (!ready) {
      return
    }
    const [allTracks, found, allTags, allTemplates] = await Promise.all([
      // Archived included. A track retired last month is still in the history
      // being exported, and dropping it puts holes in the file.
      repos.tracks.list({ includeArchived: true }),
      repos.entries.find({ range: { fromEpochMs: span.from, toEpochMs: span.to } }),
      repos.tags.list(),
      repos.templates.list({ includeArchived: true }),
    ])
    setTracks(allTracks)
    setEntries(found)
    setTags(allTags)
    setTemplates(allTemplates)
  }, [repos, ready, span.from, span.to])

  useEffect(() => {
    void reload()
  }, [reload])

  const includedIds = useMemo(
    () => (tracks ?? []).filter((t) => !excludedIds.includes(t.id)).map((t) => t.id),
    [tracks, excludedIds],
  )

  const request = useMemo<CsvRequest>(
    () => ({
      shape,
      entries,
      tracks: tracks ?? [],
      tags,
      templates,
      range: resolved,
      // Omitted entirely when nothing is excluded, so the export covers tracks
      // that do not exist yet at the moment this list was computed.
      ...(excludedIds.length === 0 ? {} : { trackIds: includedIds }),
    }),
    [shape, entries, tracks, tags, templates, resolved, excludedIds, includedIds],
  )

  const rows = useMemo(
    () => (tracks === null ? 0 : csvRowCount(request)),
    [tracks, request],
  )

  const download = async () => {
    setBusy(true)
    try {
      const result = await exporter.save(buildCsv(request))
      if (result.status === 'saved') {
        toast.show({ message: `Saved ${result.filename}` })
      } else if (result.status === 'failed') {
        toast.show({ message: result.reason, tone: 'danger' })
      }
      // A cancelled save says nothing. The user cancelled it; they know.
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <h1>Export</h1>

      {/*
        First, before the controls, because it changes what someone does next.
        Buried under the download button it would be read after the decision it
        exists to inform.
      */}
      <p className={styles['warning']} data-testid="not-a-backup">
        A CSV is a copy for a spreadsheet, <strong>not a backup</strong>. It cannot be
        read back into anytracker — it carries no track settings, check-ins or tags, so
        restoring from one would not give you your setup back.{' '}
        <Link to="/backup">Back up properly</Link> instead.
      </p>

      <RangeControl empty={tracks !== null && entries.length === 0} />

      {tracks === null ? (
        <p>Loading…</p>
      ) : (
        <>
          <fieldset className={styles['shapes']}>
            <legend className={styles['legend']}>Layout</legend>
            {SHAPES.map((choice) => (
              <label key={choice.shape} className={styles['shape']}>
                <input
                  type="radio"
                  name="shape"
                  value={choice.shape}
                  checked={shape === choice.shape}
                  onChange={() => {
                    setShape(choice.shape)
                  }}
                />
                {/* Both spans are direct children of the label rather than
                    wrapped in one, so the accessible name is reachable — the
                    grid in the stylesheet does the two-column layout. */}
                <span className={styles['shapeTitle']}>{choice.title}</span>
                <span className={styles['shapeDetail']}>{choice.detail}</span>
              </label>
            ))}
          </fieldset>

          {tracks.length > 0 && (
            <fieldset className={styles['tracks']}>
              <legend className={styles['legend']}>Tracks</legend>
              {tracks.map((track) => {
                const included = !excludedIds.includes(track.id)
                return (
                  <label key={track.id} className={styles['track']}>
                    <input
                      type="checkbox"
                      checked={included}
                      onChange={() => {
                        setExcludedIds(
                          included
                            ? [...excludedIds, track.id]
                            : excludedIds.filter((id) => id !== track.id),
                        )
                      }}
                    />
                    {track.name}
                    {track.archived && (
                      <span className={styles['archived']}>archived</span>
                    )}
                  </label>
                )
              })}
            </fieldset>
          )}

          {/*
            The count and the dates, together, immediately above the button.
            "Export" on its own is a promise about an unknown quantity of data,
            and the range is named as absolute days because "last 30 days" in a
            filename means nothing six weeks later (T-402).
          */}
          <p className={styles['summary']} data-testid="export-summary">
            {rows === 0
              ? 'Nothing recorded in this range — the file would have headings and no rows.'
              : `${String(rows)} ${rows === 1 ? 'row' : 'rows'}, ${resolved.fromDay} to ${resolved.toDay}.`}
          </p>

          <Button
            variant="primary"
            disabled={busy || rows === 0}
            onClick={() => void download()}
          >
            {busy ? 'Preparing…' : 'Download CSV'}
          </Button>
        </>
      )}
    </>
  )
}
